import type Immutable from 'immutable';

import type { accountsSelectors } from 'app-components/flux/accounts';
import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import { getBusinessIdForAccountSelection } from 'app-components/flux/accounts/accountsUtils';
import type { categoriesSelectors } from 'app-components/flux/categories';
import {
  resolveCoaForBusinessChange,
  resolveSplitItemCoasForBusinessChange,
} from 'app-components/flux/categories/categoriesUtils';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { SplitItemProps } from 'app-components/flux/transactions/transactionsTypes';
import { isAutoExcludedFromF2S } from 'app-components/flux/transactions/transactionsUtils';

export type ApplySeriesAccountChangeArgs = {
  account: AccountProps | null | undefined;
  previousAccount: AccountProps | null | undefined;
  currentCoa: ChartOfAccountProps | null | undefined;
  currentIsSubscription: boolean;
  isBillForAccount: boolean;
  touchedBill: boolean;
  touchedSubscription: boolean;
  businessEnabled: boolean;
  excludeIncomeByDefault?: boolean;
  incomeCoaIds?: Immutable.Set<string> | null;
  businessCoaIds?: Immutable.Set<string> | null;
  setFieldValue: (path: string, value: unknown) => unknown;
  categoriesById?: ReturnType<typeof categoriesSelectors.getCategoriesById>;
  accountsById?: ReturnType<typeof accountsSelectors.getAccountsById>;
  currentSplitItems?: ReadonlyArray<SplitItemProps> | null;
};

export function applySeriesAccountChange({
  account,
  previousAccount,
  currentCoa,
  currentIsSubscription,
  isBillForAccount,
  touchedBill,
  touchedSubscription,
  businessEnabled,
  excludeIncomeByDefault = false,
  incomeCoaIds,
  businessCoaIds,
  setFieldValue,
  categoriesById,
  accountsById,
  currentSplitItems,
}: ApplySeriesAccountChangeArgs): void {
  if (!account) return;

  const coaIsTransfer = Boolean(currentCoa && chartOfAccountsUtils.isTransferCOA(currentCoa));
  const coaIsAutoExcludedFromF2S = isAutoExcludedFromF2S(
    currentCoa,
    excludeIncomeByDefault,
    incomeCoaIds,
    businessCoaIds,
  );

  if (account.isExcludedFromReports) {
    setFieldValue('transaction.isExcludedFromReports', true);
    setFieldValue('transaction.excludeReportsDest', true);
  } else if (!coaIsTransfer && previousAccount?.isExcludedFromReports) {
    setFieldValue('transaction.isExcludedFromReports', false);
    setFieldValue('transaction.excludeReportsDest', false);
  }

  if (account.isExcludedFromBudgets) {
    setFieldValue('transaction.isExcludedFromF2S', true);
    setFieldValue('transaction.excludeF2SDest', true);
  } else if (!coaIsAutoExcludedFromF2S && previousAccount?.isExcludedFromBudgets) {
    setFieldValue('transaction.isExcludedFromF2S', false);
    setFieldValue('transaction.excludeF2SDest', false);
  }

  if (!touchedBill && !touchedSubscription && isBillForAccount && !currentIsSubscription) {
    setFieldValue('transaction.isBill', true);
    setFieldValue('transaction.isSubscription', false);
  }

  const nextBusinessId = getBusinessIdForAccountSelection({ account, businessEnabled });
  if (nextBusinessId === undefined) return;

  setFieldValue('transaction.businessId', nextBusinessId);

  const nextCoa = resolveCoaForBusinessChange({
    coa: currentCoa,
    categoriesById,
    accountsById,
    nextBusinessId,
  });
  if (nextCoa) {
    setFieldValue('transaction.coa', nextCoa);
  }

  const nextSplitItems = resolveSplitItemCoasForBusinessChange(currentSplitItems, {
    categoriesById,
    accountsById,
    nextBusinessId,
    preserveBusinessCategoryCoasWhenPersonal: true,
  });
  if (nextSplitItems && nextSplitItems !== currentSplitItems) {
    setFieldValue('transaction.split.items', nextSplitItems);
  }
}
